import { useItemSet } from '@exam-management/common/item-set-provider';
import { FormLeavingListener } from '@exam-management/components';
import {
  QuestionGroupType,
  type QuestionInputData,
  useCreateQuestionMutation,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useNavigate, useParams } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { DEFAULT_FORM_VALUES, CreateItemSetSubQuestionSchema } from './constants';
import type { CreateItemSetSubQuestionFormInput } from './types';
import { getLocationDefaultValue } from './utils';

type ItemSetSubQuestionCreationFormProps = PropsWithChildren;

export default function ItemSetSubQuestionCreationForm(
  props: Readonly<ItemSetSubQuestionCreationFormProps>,
) {
  const { children } = props;

  const { qBankStructureLevelItems } = useQBankStructure();
  const { itemSet } = useItemSet();
  const { questionStructures } = itemSet;

  const defaultLocationValues = useMemo(
    () => getLocationDefaultValue(questionStructures, qBankStructureLevelItems),
    [questionStructures, qBankStructureLevelItems],
  );

  const formContext = useForm<CreateItemSetSubQuestionFormInput>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      location: {
        ...DEFAULT_FORM_VALUES.location,
        ...defaultLocationValues,
      },
    },
    resolver: zodResolver(CreateItemSetSubQuestionSchema),
  });

  const { showSnackbar } = useSnackbar();
  const { questionId, structureId } = useParams(
    '/qbank-structures/:structureId/qcontents/item-set/:questionId',
  );
  const navigate = useNavigate();

  const [create] = useCreateQuestionMutation({
    onCompleted: () => {
      showSnackbar({ message: 'Question has been saved', variant: 'success' });
      navigate('/qbank-structures/:structureId/qcontents/item-set/:questionId', {
        params: {
          questionId,
          structureId,
        },
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to save question.',
        variant: 'error',
      });
    },
    update: (cache, { data }) => {
      if (data?.qctCreateQuestion) {
        const newQuestion = data.qctCreateQuestion;
        cache.modify({
          id: `QuestionGroup:${itemSet.id}`,
          fields: {
            questions(existingQuestions = [], { toReference }) {
              const newQuestionRef = toReference(newQuestion);
              return [...existingQuestions, newQuestionRef];
            },
          },
        });
      }
    },
  });

  const createSubQuestion = async (data: CreateItemSetSubQuestionFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;

    if (!qBankStructureLevelItemId) return null;

    const isCalculated =
      data.information.calculated === '' ? null : data.information.calculated === 'true';

    const group = {
      id: itemSet.id,
      type: QuestionGroupType.ItemSet,
    };

    const pValue =
      data.information.pValue.selected === 'pInitial'
        ? data.information.pValue.pInitial
        : data.information.pValue.pStat;

    const cognitiveDomains = data.properties.cognitiveDomain.map((domain) => domain.value);

    const convertedData: QuestionInputData = {
      details: parseContentForApi(data.detail.details),
      choices: data.detail.choices.map((choice) => ({
        ...choice,
        label: parseContentForApi(choice.label),
      })),
      correctAnswer: data.detail.correctAnswer,
      calculated: isCalculated,
      pValue: typeof pValue === 'number' ? pValue : undefined, // NOTE: pValue can be number, empty string, or undefined
      bIndexValue: data.information.bIndex !== '' ? data.information.bIndex : undefined,
      cognitiveDomains,
      learningObjectiveStatement: data.properties.learningObjectiveStatement?.value ?? undefined,
      solution: parseContentForApi(data.properties.solution),
      reference: parseContentForApi(data.properties.reference),
      group,
      ...data.owners,
    };

    const result = await create({
      variables: {
        input: {
          qBankStructureLevelItemId,
          refQBankStructureId: structureId,
          data: convertedData,
        },
      },
    });

    return result.data?.qctCreateQuestion.id;
  };

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleSubmit = async (data: CreateItemSetSubQuestionFormInput) => {
    await createSubQuestion(data);
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSubmit, handleShowRequiredFieldsError)}>
        {children}
      </Form>
      <FormLeavingListener onSubmit={handleSubmit} />
    </FormProvider>
  );
}
